import type { MaterialReceiptRow } from '../types/api';
import { ProxyJob, ProxyBand, DO_NOT_FOLLOW_UP_TAG, PROXY_SEED_TODAY } from '../data/mockProxyJobs';
import { decide, bandFor, daysToMeeting, DecisionResult } from './proxyDecisioning';
import { isSkippedMaterial } from '../data/demoShortages';

export type EffectiveStatus = 'ACTIVE' | 'RECEIVED' | 'SUPPRESSED';

export interface ResolvedProxyJob extends ProxyJob {
  band: ProxyBand;
  decision: DecisionResult;
  clearedViaIntake: boolean;
  clearedNote: string | null;
  effectiveStatus: EffectiveStatus;
}

// Cross-references a job against LIVE Material Intake data (the real
// getMaterialReceipts() API — zero backend changes) so the "shared job spine"
// auto-clear requirement is genuinely functional, not just structurally
// plausible. A handful of mock jobIds in mockProxyJobs.ts are deliberately
// anchored to real job_numbers that exist in the backend seed data for this.
function isClearedViaIntake(job: ProxyJob, liveReceipts: MaterialReceiptRow[] | null): boolean {
  if (!liveReceipts) return false;
  return liveReceipts.some(r =>
    r.job_category === 'P' &&
    Array.isArray(r.job_numbers) && r.job_numbers.includes(job.jobId) &&
    r.material_status === 'Matched',
  );
}

export function resolveProxyJob(job: ProxyJob, liveReceipts: MaterialReceiptRow[] | null): ResolvedProxyJob {
  const band = bandFor(daysToMeeting(job));
  const decision = decide({
    daysToVoteCutoff: job.daysToVoteCutoff,
    daysToMeeting: daysToMeeting(job),
    filingPulled: job.followUpHistory.some(r => r.type === 'filing_pulled'),
    filingOnPortal: job.filingOnPortal,
    materialStatus: job.materialStatus,
    pageCount: job.pageCount,
    fullSetHolders: job.fullSetHolders,
    country: job.country,
    isBroadridge: job.isBroadridge,
    mailDate: job.mailDate,
    econsentOnly: job.econsentOnly,
    solicitor: job.solicitor,
    today: PROXY_SEED_TODAY,
    heldForDiscovery: job.heldForDiscovery,
  });

  const clearedViaIntake = isClearedViaIntake(job, liveReceipts);
  const effectiveStatus: EffectiveStatus = clearedViaIntake
    ? 'RECEIVED'
    : job.opsNotes.includes(DO_NOT_FOLLOW_UP_TAG) ? 'SUPPRESSED' : 'ACTIVE';

  return {
    ...job,
    band,
    decision,
    clearedViaIntake,
    clearedNote: clearedViaIntake ? 'Received — cleared via Material Intake' : null,
    effectiveStatus,
  };
}

// A job's only outstanding material is VIF/SMRF — per ICT-13, these don't
// count as outstanding, so the job drops out of the active queue entirely.
export function hasOnlySkippedMaterial(job: ProxyJob): boolean {
  const types = job.shortageLines?.length
    ? job.shortageLines.map(l => l.materialType)
    : job.outstandingMaterialTypes;
  return types.length > 0 && types.every(isSkippedMaterial);
}

